import { Component, ElementRef, EventEmitter, Input, OnInit, Optional, Output, Renderer2, Self, SimpleChanges, ViewChild } from "@angular/core"
import { AbstractControl, UntypedFormControl, NgControl, ValidationErrors, Validator } from "@angular/forms"
import { IconImageService } from "@shared/services/icon-image.service"
import { ImageFilterService } from "@shared/services/image-filter.service"

@Component({
  selector: "ag-input-field",
  templateUrl: "./ag-input-field.component.html",
  styleUrls: ["./ag-input-field.component.scss"],
  standalone: false,
})
export class AgInputFieldComponent implements Validator, OnInit {
  @Input() label: string = ""
  @Input() placeholder: string = ""
  @Input() hint?: string = ""
  @Input() inputType?: InputType = "text"
  @Input() imgPath: string = ""
  @Input() disable: boolean = false
  @Input() initValue: string = ""
  @Input() isPassword?: boolean = false
  @Input() fieldId: string = "input"
  @Input() maxLength?: number
  @Input() simpleVersion: boolean = false
  @Input() allowNegativeNumbers: boolean = true //when inputtype is number this can be set
  @Input() detectAutofillInput: boolean = true
  @Input() showDecimalValues: boolean = true //when inputtype is number this can be set

  @Input() styles: InputStyles = {
    labelColor: "",
    backgroundColor: "",
    border: "",
    specificWidth: "314px",
    specificHeight: "",
  }
  @Input() info: InputInfo = {
    showInfo: false,
    type: "info",
    text: "Info",
  }
  @Input() infoColor?: string

  //This Input is only needed for multiple inputs in one component
  @Input() numberOfComponents: Array<number> = [0]
  //if you want to more have more inputs and give them error make sure
  //that the numbers in this array match the index of the inputs from numberOfComponents
  @Input() invalidInputIndex: Array<number>

  @Output() inputResult = new EventEmitter()
  @Output() changeResult = new EventEmitter()
  @Output() enterPressed = new EventEmitter()

  @ViewChild("viewControl") viewControl?: ElementRef
  @ViewChild("agValue") agValue?: ElementRef

  public control: UntypedFormControl
  public isInputFocused: boolean = false
  public isInputHovered: boolean = false
  public inputMessage: string
  private valueOnFocus: string
  public labelClicked: boolean = false
  public isAutoFilled: boolean = false

  public focusedIndex: number
  public hoveredIndex: number

  constructor(
    @Optional() @Self() private _ngControl: NgControl,
    private renderer: Renderer2,
    public iconService: IconImageService,
    public imageFilter: ImageFilterService,
    private el: ElementRef,
  ) {
    if (this._ngControl) {
      this._ngControl.valueAccessor = this
    }
  }

  ngOnInit(): void {
    if (this.initValue?.length > 0) {
      this.labelClicked = true
      this.inputMessage = this.initValue
    }
    if (this._ngControl?.control) {
      this.control = this._ngControl.control as UntypedFormControl
      if (this.disable) this.control.disable()
    } else {
      this.control = new UntypedFormControl({ value: this.initValue, disabled: this.disable })
    }
    this.control.valueChanges.subscribe((x) => {
      if (this.numberOfComponents.length === 0) {
        this.inputResult.emit(x)
      }
    })
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes["disable"]) {
      if (this.disable) {
        this.control?.disable()
      } else {
        this.control?.enable()
      }
    }
    if (!changes["initValue"]?.firstChange && changes["initValue"]?.currentValue !== changes["initValue"]?.previousValue) {
      this.inputMessage = changes["initValue"].currentValue
      this.control.setValue(this.inputMessage)
    }
  }

  writeValue() {}
  registerOnChange() {}
  registerOnTouched() {}

  validate(control: AbstractControl): ValidationErrors | null {
    return this.control.valid ? null : control.errors
  }

  ngAfterViewInit(): void {
    setTimeout(() => {
      if (this.control) {
        this.initValue = this.control.value
        this.inputMessage = this.control.value
        this.labelClicked = true
      }
    }, 1000)
    if (this.detectAutofillInput && this.detectAutofill(document.getElementById(this.fieldId))) {
      this.isAutoFilled = true
    }
  }

  onFocus() {
    this.isInputFocused = true
    this.valueOnFocus = this.inputMessage
  }

  onBlur() {
    this.isInputFocused = false
    this.labelClicked = false
    this.emitPendingChange()
  }

  private emitPendingChange(): void {
    if (this.inputMessage === this.valueOnFocus) return
    this.valueOnFocus = this.inputMessage
    this.changeResult.emit(this.inputMessage)
  }

  numberedEnter(index: number) {
    this.hoveredIndex = index
  }

  numberedLeave() {
    this.hoveredIndex = null
  }

  numberedFocus(index: number) {
    this.focusedIndex = index
  }

  numberedBlur() {
    this.focusedIndex = null
  }

  onEnter() {
    this.isInputHovered = true
  }

  onLeave() {
    this.isInputHovered = false
  }

  public showPlaceholder(): string {
    if (this.isInputFocused || !this.label) {
      return this.placeholder
    } else {
      return ""
    }
  }

  public clickInput() {
    this.labelClicked = true
    this.el.nativeElement.querySelector("input").focus()
  }

  onInput(event: Event) {
    const input = event.target as HTMLInputElement

    if (this.inputType === "number") {
      input.value = this.showDecimalValues ? input.value.replace(/[^0-9,.-]/g, "") : input.value.replace(/[^0-9-]/g, "")
    }

    this.inputMessage = input.value
    this.inputResult.emit(this.inputMessage)
  }

  onChange(event: Event) {
    const input = event.target as HTMLInputElement

    if (this.inputType === "number") {
      input.value = this.showDecimalValues ? input.value.replace(/[^0-9,.-]/g, "") : input.value.replace(/[^0-9-]/g, "")

      let rawValue = input.value
      let numericValue

      if (this.showDecimalValues) {
        if (rawValue.includes(",") && rawValue.includes(".")) {
          rawValue = rawValue.replace(/\./g, "").replace(",", ".")
        } else if (rawValue.includes(",")) {
          rawValue = rawValue.replace(",", ".")
        }
        numericValue = parseFloat(rawValue)
      } else {
        numericValue = parseInt(rawValue, 10)
      }

      if (isNaN(numericValue)) {
        numericValue = 0
      }

      if (!this.allowNegativeNumbers && numericValue < 0) {
        numericValue = 0
      }

      const formatted = new Intl.NumberFormat("de-DE", {
        minimumFractionDigits: this.showDecimalValues ? 2 : 0,
        maximumFractionDigits: this.showDecimalValues ? 2 : 0,
      }).format(numericValue)

      setTimeout(() => {
        input.value = formatted
      })

      this.control.setValue(numericValue, { emitEvent: false })
      this.inputMessage = formatted
    } else this.inputMessage = input.value

    this.valueOnFocus = this.inputMessage
    this.changeResult.emit(this.inputMessage)
  }

  detectAutofill = (element) => {
    return new Promise((resolve) => {
      setTimeout(() => {
        resolve(window.getComputedStyle(element, null).getPropertyValue("appearance") === "menulist-button")
      }, 600)
    })
  }

  isInputValid(input: string): boolean {
    return !isNaN
  }

  changeNumberedInput(event: any, index: number) {
    if (event.inputType === "deleteContentBackward") {
      return
    }
    const inputElement = event.target as HTMLInputElement
    const previousValue = inputElement.dataset.previousValue || ""
    let value = inputElement.value
    if (value.length >= 2 && index === 5) {
      value = value.charAt(1)
      inputElement.value = value
    }
    const isDelete = value.length < previousValue.length
    if (isDelete) {
      const prevIndex = index - 1
      const prevInput = document.getElementById(`input-${prevIndex}`) as HTMLInputElement | null
      this.inputResult.emit({ val: value, index: index })
      if (prevInput) {
        prevInput.focus()
      }
    } else {
      if (value.length > 2 && index === 0) {
        inputElement.value = value.charAt(0)
        inputElement.dataset.previousValue = value.length > 1 ? value.charAt(0) : value
        this.inputResult.emit({ val: value.charAt(0), index: index }) // Emit value and index for current input
        for (let i = 1; i < value.length; i++) {
          const nextIndex = index + i
          const nextInput = document.getElementById(`input-${nextIndex}`) as HTMLInputElement | null
          if (nextInput) {
            nextInput.value = value.charAt(i)
            nextInput.dataset.previousValue = value.charAt(i)
            nextInput.focus()
          }
          this.inputResult.emit({ val: value.charAt(i), index: nextIndex }) // Emit value and index for next input
        }
      } else {
        inputElement.dataset.previousValue = value.length > 1 ? value.charAt(0) : value
        const nextIndex = index + 1
        const nextInput = document.getElementById(`input-${nextIndex}`) as HTMLInputElement | null
        if (nextInput) {
          nextInput.focus()
        }
        if (value.length >= 2) {
          value = value.charAt(1)
          inputElement.value = value
        }
        this.inputResult.emit({ val: value, index: index })
      }
    }
  }
  enterPressedFunc() {
    this.enterPressed.emit()
  }
  onKeyDown(event: KeyboardEvent, index: number) {
    const inputElement = event.target as HTMLInputElement
    if (event.key === "Enter") {
      this.enterPressed.emit()
    }
    if (event.key === "Backspace" && inputElement.value.length === 0) {
      const prevIndex = index - 1
      const prevInput = document.getElementById(`input-${prevIndex}`) as HTMLInputElement | null
      if (prevInput) {
        setTimeout(() => {
          prevInput.value = prevInput.value
          prevInput.focus()
        }, 0)
      }
    }
    if (event.key === "Backspace" && inputElement.value.length === 1) {
      inputElement.value = ""
    }

    if (event.key === "ArrowRight") {
      const nextIndex = index + 1
      const nextInput = document.getElementById(`input-${nextIndex}`) as HTMLInputElement | null
      if (nextInput) {
        nextInput.focus()
      }
    }
    if (event.key === "ArrowLeft") {
      const prevIndex = index - 1
      const prevInput = document.getElementById(`input-${prevIndex}`) as HTMLInputElement | null
      if (prevInput) {
        setTimeout(() => {
          prevInput.value = prevInput.value
          prevInput.focus()
        }, 0)
      }
    }
  }
}

export type InputType = "text" | "url" | "tel" | "password" | "email" | "number"

export class InputStyles {
  labelColor?: string
  backgroundColor?: string
  border?: string
  specificWidth?: string
  specificHeight?: string
}

export class InputInfo {
  showInfo?: boolean
  type?: Type
  text?: string
}

export type Type = "info" | "error"
